import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft, FiX } from "react-icons/fi";

import { post as commsPost } from "../../../service/comms/commsApi";
import { createBroadcast } from "../../../service/comms/broadcastsService";
import useCompanyId from "../../../hooks/comms/useCompanyId";
import useBroadcastEstimate from "./useBroadcastEstimate";
import useBroadcastImage from "./useBroadcastImage";
import { EMPTY_DRAFT, validateDraft } from "./broadcastDraft";
import { buildBroadcastPayload } from "./broadcastPayload";
import {
  createResultFromResponse,
  draftErrorsFromFieldErrors,
} from "./broadcastDecode";
import { messageFor, fieldErrorsFromError, isValidation } from "./broadcastErrors";
import { errorFor, usesEmail } from "./broadcastDraft";
import { PushContentCard } from "./PushContentCard";
import { AudienceCard } from "./AudienceCard";
import { EmailContentCard } from "./EmailContentCard";
import { ComposeModeSwitch } from "./ComposeModeSwitch";
import { FilterPicker } from "./FilterPicker";
import { ComposerPreviewRail } from "./ComposerPreviewRail";
import { ImageDropzone } from "./ImageDropzone";
import { SendFooter } from "./SendFooter";
import { ConfirmSendDialog } from "./ConfirmSendDialog";

/**
 * The broadcast composer.
 *
 * Container only: it owns the draft, the submit call and the three outcomes a
 * create can have. Every field, number and preview is rendered by a child, and
 * every wire field name is produced by `broadcastPayload.js`.
 *
 * ---------------------------------------------------------------------------
 * ONE MODE AT A TIME
 * ---------------------------------------------------------------------------
 * The first control on the page decides whether this is a popup or an email,
 * and that decision picks the entire form underneath it: which content card,
 * which image upload, which audience, which preview. There is no "both" — the
 * two surfaces are written separately because they read separately, and an
 * author who wants both sends two.
 *
 * The dormant mode's fields stay in the draft untouched. Switching back and
 * forth does not lose work, and nothing from the mode that is not selected is
 * validated, previewed or sent.
 *
 * Create-only by design. v1_10_0 is assumed to have dropped PATCH/DELETE on a
 * broadcast, so there is no edit path here; `/broadcasts/:id/edit` is not
 * routed to this component.
 */

/**
 * An `ImageDropzone` wired to one `useBroadcastImage` instance.
 *
 * Ten props, spelled once. The two modes each own an upload and the wiring is
 * identical; writing it twice is how one of them ends up missing `unsupported`
 * and silently offering a dropzone the backend will refuse.
 *
 * @param {Object} props
 * @param {ReturnType<typeof useBroadcastImage>} props.image
 * @returns {React.ReactElement}
 */
const Dropzone = ({ image }) => (
  <ImageDropzone
    previewUrl={image.previewUrl}
    dataUrl={image.dataUrl}
    blobPath={image.blobPath}
    fileName={image.fileName}
    uploading={image.uploading}
    error={image.error}
    warning={image.warning}
    unsupported={image.unsupported}
    onPick={image.pick}
    onClear={image.clear}
  />
);

export default function BroadcastComposer() {
  const navigate = useNavigate();
  const { companyId, loading: companyLoading } = useCompanyId();

  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** A create that failed outright. */
  const [error, setError] = useState(null);
  /**
   * Server complaints from a 422, already translated from wire field names to
   * draft field names so the cards render them through the same `errorFor`
   * path as local validation. Shaped `[{field, message}]`.
   */
  const [serverErrors, setServerErrors] = useState([]);
  /**
   * A create that returned 201 with `enqueued: false`: the broadcast exists
   * but nothing is being delivered. Reporting that as success is the failure
   * mode this state exists to prevent.
   */
  const [stalled, setStalled] = useState(null);

  const { estimate, estimating, failed } = useBroadcastEstimate(draft);

  const patch = useCallback((partial) => {
    setDraft((current) => ({ ...current, ...partial }));
    // Any edit invalidates a server-side complaint about the old values.
    setServerErrors([]);
    setError(null);
  }, []);

  // ONE UPLOAD, AND IT BELONGS TO THE POPUP.
  //
  // The upload owns its own state; the draft only ever holds the finished
  // value. That way a half-finished or failed upload can never be sent, and
  // the image field means "an image that is really in storage" and nothing
  // else.
  //
  // There used to be a second instance for the email. The email has no image
  // any more — it never displayed one at a size worth the upload — so there is
  // one hook, wired to the one surface that shows a picture.
  const pushImage = useBroadcastImage({
    onUploaded: useCallback(
      ({ dataUrl, blobPath }) =>
        patch({ imageUrl: dataUrl, imageBlobPath: blobPath }),
      [patch]
    ),
    onCleared: useCallback(
      () => patch({ imageUrl: "", imageBlobPath: "" }),
      [patch]
    ),
  });

  const localErrors = useMemo(() => validateDraft(draft), [draft]);

  // Local and server complaints render identically. Local ones come first so a
  // field the user can already see is wrong wins over a stale server message.
  const errors = useMemo(
    () => [...localErrors, ...serverErrors],
    [localErrors, serverErrors]
  );

  // `canSend` is gated on LOCAL validity only. A server complaint must not
  // permanently disable the button — the user has to be able to retry after
  // correcting, and a 422 about a field we cannot map would otherwise wedge
  // the composer with no visible cause.
  //
  // The company id gates submission too: sending `company_id: null` is a
  // documented prior bug that produced broadcasts nobody received.
  const canSend = localErrors.length === 0 && !companyLoading && Boolean(companyId);
  const scheduled = draft.scheduleMode === "later";
  /** Which of the two things is being written. Decides the whole form below. */
  const email = usesEmail(draft);
  const modeError = errorFor(errors, "channels");

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    setStalled(null);
    try {
      const payload = buildBroadcastPayload(draft, { companyId });
      const result = createResultFromResponse(await createBroadcast(commsPost, payload));

      if (result.enqueued === false) {
        // Created, but delivery never started. The broadcast is real and
        // re-sendable, so this is not an error — and it is not a success
        // either. Stay on the page and say so.
        setStalled(result.id);
        setConfirming(false);
        return;
      }

      navigate("/broadcasts", {
        state: {
          flash: scheduled ? "Broadcast scheduled." : "Broadcast sent.",
        },
      });
    } catch (err) {
      // A 422 names the offending field and the limit. Show it verbatim next
      // to the input rather than paraphrasing it into a generic apology. The
      // banner still carries the full detail, so a field we cannot map is
      // reported rather than swallowed.
      if (isValidation(err)) {
        // The mode decides which input a complaint about the content slot
        // belongs to — see `draftFieldFromWire`.
        setServerErrors(
          draftErrorsFromFieldErrors(fieldErrorsFromError(err), { email })
        );
      }
      setError(messageFor(err));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-bc-page px-10 py-9">
      <div className="mx-auto max-w-[1120px]">
        <button
          type="button"
          onClick={() => navigate("/broadcasts")}
          className="mb-4 inline-flex items-center gap-1.5 text-xs text-bc-muted hover:text-bc-text"
        >
          <FiArrowLeft aria-hidden="true" />
          Broadcasts
        </button>

        <header className="mb-5 flex items-start justify-between gap-5">
          <div>
            <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-bc-text">
              New broadcast
            </h1>
            <p className="mt-1 text-xs text-bc-soft">
              Pick a surface, write it for that surface, send it to one
              audience.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/broadcasts")}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-[7px] border border-bc-border bg-white px-4 py-2 text-[13px] font-medium text-bc-text-2"
          >
            <FiX aria-hidden="true" />
            Close
          </button>
        </header>

        {stalled ? (
          <div
            role="status"
            className="mb-4 flex items-start gap-2.5 rounded-lg border border-bc-warn/40 bg-bc-warn-bg px-4 py-3 text-[13px] text-bc-warn"
          >
            <FiAlertTriangle className="mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">
                Saved, but delivery has not started.
              </p>
              <p className="mt-0.5">
                The broadcast exists and can be sent again. Retry send.
              </p>
            </div>
          </div>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-bc-bad/40 bg-bc-bad-bg px-4 py-3 text-[13px] text-bc-bad"
          >
            {error}
          </div>
        ) : null}

        <div className="mb-5">
          <ComposeModeSwitch
            value={draft.channels}
            onChange={(id) => patch({ channels: id })}
          />
          {/* Unreachable from the switch, which offers two valid values — but
              `canSend` is gated on it, and a disabled button with no stated
              reason is the failure this line exists to prevent. */}
          {modeError ? (
            <p role="alert" className="mt-2 text-xs text-bc-bad">
              {modeError}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-start gap-6">
          <div className="flex min-w-[380px] flex-1 flex-col gap-4">
            {email ? (
              <EmailContentCard draft={draft} onPatch={patch} errors={errors} />
            ) : (
              <PushContentCard
                draft={draft}
                onPatch={patch}
                errors={errors}
                imageSlot={<Dropzone image={pushImage} />}
              />
            )}

            <AudienceCard
              draft={draft}
              onPatch={patch}
              errors={errors}
              estimate={estimate}
              estimating={estimating}
              failed={failed}
              filterSlot={
                email ? (
                  <FilterPicker
                    channel="email"
                    value={draft.emailFilterId || null}
                    onChange={(id) => patch({ emailFilterId: id })}
                  />
                ) : (
                  <FilterPicker
                    channel="push"
                    value={draft.pushFilterId || null}
                    onChange={(id) => patch({ pushFilterId: id })}
                  />
                )
              }
            />

            <SendFooter
              draft={draft}
              onPatch={patch}
              errors={errors}
              estimate={estimate}
              estimating={estimating}
              failed={failed}
              canSend={canSend}
              submitting={submitting}
              onSend={() => setConfirming(true)}
            />
          </div>

          <div className="w-[380px] min-w-[320px] flex-1">
            {/* The image is the popup's. An email draft passes nothing, which
                is not "no image yet" — an email has no image field at all.

                NOT `draft.imageUrl`: that is the presign's `data_url`, the
                blob's identity rather than a fetchable link, and a GET on it
                answers 409 against the private container. The local object URL
                is the one source that is displayable without a SAS — same rule
                `ImageDropzone` follows. */}
            <ComposerPreviewRail
              draft={draft}
              imageUrl={email ? "" : pushImage.previewUrl}
            />
          </div>
        </div>
      </div>

      <ConfirmSendDialog
        open={confirming}
        draft={draft}
        estimate={failed ? null : estimate}
        scheduled={scheduled}
        submitting={submitting}
        onConfirm={submit}
        onCancel={() => setConfirming(false)}
      />
    </main>
  );
}
